'use client';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserRole, demoRoleMap, demoUserNames, roleDashboardPaths } from '@/lib/roleConfig';

interface RoleContextValue {
  role: UserRole;
  userName: string;
  userEmail: string;
  dashboardPath: string;
  setRoleFromEmail: (email: string) => void;
  clearRole: () => void;
}

const RoleContext = createContext<RoleContextValue>({
  role: 'admin',
  userName: 'Super Admin',
  userEmail: 'admin@srisevatrust.in',
  dashboardPath: '/admin-dashboard',
  setRoleFromEmail: () => {},
  clearRole: () => {},
});

export const useRole = () => useContext(RoleContext);

export const RoleProvider = ({ children }: { children: React.ReactNode }) => {
  const [role, setRole] = useState<UserRole>('admin');
  const [userName, setUserName] = useState('Super Admin');
  const [userEmail, setUserEmail] = useState('admin@srisevatrust.in');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedEmail = sessionStorage.getItem('userEmail');
      if (savedEmail) {
        applyEmail(savedEmail);
      }
    }
  }, []);

  const applyEmail = (email: string) => {
    const detectedRole = demoRoleMap[email] ?? 'member';
    const detectedName = demoUserNames[email] ?? email.split('@')[0];
    setRole(detectedRole);
    setUserName(detectedName);
    setUserEmail(email);
  };

  const setRoleFromEmail = (email: string) => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('userEmail', email);
    }
    applyEmail(email);
  };

  const clearRole = () => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('userEmail');
    }
    setRole('admin');
    setUserName('Super Admin');
    setUserEmail('admin@srisevatrust.in');
  };

  return (
    <RoleContext.Provider value={{
      role,
      userName,
      userEmail,
      dashboardPath: roleDashboardPaths[role],
      setRoleFromEmail,
      clearRole,
    }}>
      {children}
    </RoleContext.Provider>
  );
};
